<template>
  <div>
    <div v-if="isLoading" class="loader"></div>
    <div v-if="isLoading" class="loadertext">
      {{ $t("loadingTxt") }}...
    </div>

    <div class="main-wrapper" :class="{ 'blur-content': isLoading }">
      <sidebar-component classMenu="TemplateSetting"></sidebar-component>

      <div class="page-wrapper">
        <navbar-component></navbar-component>

        <div class="page-content">
          <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
            <div>
              <button class="btn btn-link px-0 text-decoration-none mb-2" @click="goBack">
                <i class="mdi mdi-arrow-left me-1"></i>
                Back to Customer List
              </button>
              <h4 class="mb-0">Create - PI Non STD</h4>
            </div>
          </div>

          <div class="card customer-detail-card">
            <div class="card-body">
              <div class="row align-items-end mb-3">
                <div class="col-xl-6 col-lg-7">
                  <label class="form-label fw-semibold">Customer</label>
                  <input type="text" class="form-control" :value="displayCustomer" disabled />
                </div>
              </div>

              <div class="row align-items-end mb-3">
                <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
                  <label class="form-label">PI Number</label>
                  <input v-model.trim="filters.pi_no" type="text" class="form-control" placeholder="PI Number"
                    @keyup.enter="loadItems" />
                </div>
                <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
                  <label class="form-label">Load ID</label>
                  <input v-model.trim="filters.load_id" type="text" class="form-control" placeholder="Load ID"
                    @keyup.enter="loadItems" />
                </div>
                <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
                  <label class="form-label">Status</label>
                  <select v-model="filters.status" class="form-select" @change="loadItems">
                    <option value="">Semua Status</option>
                    <option value="CREATED">CREATED</option>
                    <option value="POSTED">POSTED</option>
                    <option value="PRINTED">PRINTED</option>
                  </select>
                </div>
                <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
                  <label class="form-label">Tanggal PI Dari</label>
                  <input v-model="filters.date_from" type="date" class="form-control" @change="loadItems" />
                </div>
                <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
                  <label class="form-label">Tanggal PI Sampai</label>
                  <input v-model="filters.date_to" type="date" class="form-control" @change="loadItems" />
                </div>
                <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
                  <div class="d-flex gap-2">
                    <button class="btn btn-primary" @click="loadItems">Cari</button>
                    <button class="btn btn-light" @click="resetFilter">
                      Hapus Filter
                    </button>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end flex-wrap gap-2 mb-3">
                <button class="btn btn-warning text-dark fw-semibold" v-if="permision_role.method_create"
                  @click="goToCreate">
                  <i class="mdi mdi-plus-box-outline me-1"></i>
                  Buat Proforma Invoice
                </button>
                <button class="btn btn-success" :disabled="!canPrintXlsx" @click="downloadSelectedXlsx">
                  <i class="mdi mdi-file-excel-outline me-1"></i>
                  Print XLSX
                </button>
                <button class="btn btn-primary" v-if="selectedInvoices.length > 0" :disabled="!canConfirm"
                  @click="confirmSelected"
                  :title="!canConfirm ? 'Tidak dapat confirm: terdapat PI dengan status PRINTED atau POSTED' : ''">
                  <i class="mdi mdi-check-circle-outline me-1"></i>
                  Confirm
                </button>
                <button class="btn btn-danger" disabled>Print PDF</button>
                <button
                  class="btn btn-success"
                  :disabled="!canDownloadMultiSheet"
                  @click="downloadMultiSheet"
                >
                  <i class="mdi mdi-file-excel-outline me-1"></i>
                  Download Multi Sheet
                </button>
                <!-- <button class="btn btn-primary" disabled>Posting</button> -->
              </div>

              <vue-good-table mode="remote" ref="invoiceTable" :columns="columns" :rows="invoices" :line-numbers="true"
                :totalRows="totalRecords" :isLoading.sync="isLoading" :pagination-options="{
                  enabled: true,
                  perPage: serverParams.per_page,
                  perPageDropdown: [],
                }" :select-options="{
                  enabled: true,
                  selectOnCheckboxOnly: true,
                  selectionText: $t('data_selected'),
                  clearSelectionText: $t('clear'),
                }" styleClass="vgt-table striped bordered" @on-page-change="onPageChange"
                @on-selected-rows-change="onSelectionChanged">
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'actions'">
                    <div class="d-flex gap-1">
                      <button type="button" class="btn btn-icon btn-sm btn-outline-secondary"
                        @click="goToView(props.row)" title="View">
                        <i class="mdi mdi-eye-outline"></i>
                      </button>
                      <button v-if="canEditRow(props.row)" type="button" class="btn btn-icon btn-sm btn-outline-primary"
                        @click="goToEdit(props.row)" title="Edit">
                        <i class="mdi mdi-square-edit-outline"></i>
                      </button>
                      <button v-if="permision_role.method_delete" type="button"
                        class="btn btn-icon btn-sm btn-outline-danger" @click="deleteInvoice(props.row)" title="Delete">
                        <i class="mdi mdi-trash-can-outline"></i>
                      </button>
                    </div>
                  </span>
                  <span v-else-if="props.column.field === 'status'">
                    <span :class="statusClass(props.row.status)">
                      {{ props.row.status }}
                    </span>
                  </span>
                  <span v-else-if="props.column.field === 'entries'">
                    {{ props.row.entries }}
                  </span>
                  <span v-else-if="props.column.field === 'total_summary'">
                    {{ formatCurrency(props.row.total_summary) }}
                  </span>
                  <span v-else-if="props.column.field === 'created_at'">
                    {{ formatDate(props.row.created_at) }}
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] || "-" }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>

        <footer-component></footer-component>
      </div>
    </div>
  </div>
</template>

<script>
import sidebarComponent from "@/components/_partials/_sidebar";
import navbarComponent from "@/components/_partials/_navbar";
import footerComponent from "@/components/_partials/_footer";



export default {
  name: "CustomerListDetail",
  components: {
    "sidebar-component": sidebarComponent,
    "navbar-component": navbarComponent,
    "footer-component": footerComponent,
  },
  data() {
    return {
      isLoading: false,
      totalRecords: 0,
      permision_role: {
        method_create: true,
        method_read: true,
        method_update: true,
        method_delete: true,
      },
      filters: {
        pi_no: "",
        load_id: "",
        status: "",
        date_from: "",
        date_to: "",
      },
      customer: {
        company_code: this.$route.params.id || "-",
        customer_name: this.$route.query.name || this.$route.params.id || "-",
        sap_code: this.$route.query.sap_code || "-",
      },
      invoices: [],
      selectedInvoices: [],
      serverParams: {
        page: 1,
        per_page: 10,
      },
    };
  },
  computed: {
    columns() {
      return [
        { label: "Tindakan", field: "actions", sortable: false },
        { label: "Nomor Proforma Invoice", field: "pi_no" },
        { label: "Report Period", field: "month_periode" },
        { label: "Entries", field: "entries", type: "number" },
        { label: "Total", field: "total_summary" },
        { label: "Status", field: "status" },
        { label: "Dibuat Pada", field: "created_at" },
        { label: "Branch", field: "branch" },
        { label: "Template", field: "template_name" },
      ];
    },
    proformaInvoiceEndpoint() {
      return String(this.$settings?.endPointProformaInvoice || "").replace(/\/?$/, "/");
    },
    displayCustomer() {
      return `${this.customer.company_code || "-"} - ${this.customer.customer_name || "-"}`;
    },
    canPrintXlsx() {
      return this.selectedInvoices.length > 0;
    },
    canConfirm() {
      if (!this.selectedInvoices.length) return false;
      const blockedStatuses = ["PRINTED", "POSTED"];
      return !this.selectedInvoices.some((inv) =>
        blockedStatuses.includes(inv.status)
      );
    },
    canDownloadMultiSheet() {
      return this.selectedInvoices.length > 0;
    },
  },
  methods: {
    goBack() {
      this.$router.push("/proforma-invoice/customer-list");
    },
    goToCreate() {
      this.$router.push({
        name: "CustomerListInvoiceCreate",
        params: {
          id: this.customer.company_code,
        },
        query: {
          name: this.customer.customer_name,
          sap_code: this.customer.sap_code,
        },
      });
    },
    goToEdit(row) {
      this.$router.push({
        name: "CustomerListInvoiceEdit",
        params: {
          id: this.customer.company_code,
          piNo: row.pi_no,
        },
        query: {
          name: this.customer.customer_name,
          sap_code: this.customer.sap_code,
        },
      });
    },
    goToView(row) {
      this.$router.push({
        name: "CustomerListInvoiceView",
        params: {
          id: this.customer.company_code,
          piNo: row.pi_no,
        },
        query: {
          name: this.customer.customer_name,
          sap_code: this.customer.sap_code,
        },
      });
    },
    canEditRow(row) {
      return row.status === "CREATED" && this.permision_role.method_update;
    },
    canDeleteRow(row) {
      return row.status === "CREATED" && this.permision_role.method_delete;
    },
    resetFilter() {
      this.filters.pi_no = "";
      this.filters.load_id = "";
      this.filters.status = "";
      this.filters.date_from = "";
      this.filters.date_to = "";
      this.serverParams.page = 1;
      this.loadItems();
    },
    statusClass(status) {
      return {
        badge: true,
        "bg-soft-primary text-primary": status === "CREATED",
        "bg-soft-warning text-warning": status === "POSTED",
        "bg-soft-success text-success": status === "PRINTED",
      };
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const parsed = this.$moment(value);
      return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
    },
    formatCurrency(value) {
      const numericValue = Number(value || 0);

      if (!Number.isFinite(numericValue)) {
        return value || "-";
      }

      return numericValue.toLocaleString("id-ID", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    onPageChange(params) {
      this.serverParams.page = params.currentPage;
      this.loadItems();
    },
    onSelectionChanged(params) {
      this.selectedInvoices = params.selectedRows || [];
    },
    getDownloadFilename(response, piNo) {
      const disposition = response?.headers?.["content-disposition"] || "";
      const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
      const extractedName = utfMatch?.[1]
        ? decodeURIComponent(utfMatch[1])
        : asciiMatch?.[1];

      if (extractedName) {
        return extractedName;
      }

      const currentDate = this.$moment().format("DD_MM_YYYY_HH_mm_ss");
      return `proforma_invoice_${piNo}_${currentDate}.xlsx`;
    },
    triggerFileDownload(blobData, fileName) {
      const fileURL = window.URL.createObjectURL(new Blob([blobData]));
      const fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute("download", fileName);
      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
      window.URL.revokeObjectURL(fileURL);
    },
    async downloadSelectedXlsx() {
      if (!this.canPrintXlsx) {
        this.error("Pilih minimal satu PI terlebih dahulu.");
        return;
      }

      const selectedRows = [...this.selectedInvoices];

      this.$swal({
        icon: "warning",
        title: "Please Wait",
        text: "Don't Close This Page !!!",
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCancelButton: false,
        showConfirmButton: false,
        didOpen: async () => {
          this.$swal.showLoading();

          try {
            for (const item of selectedRows) {
              const response = await this.$http.get(
                `${this.proformaInvoiceEndpoint}v1/company-list/download-excel-proforma-invoice`,
                {
                  params: {
                    customer_id: this.customer.company_code,
                    pi_no: item.pi_no,
                  },
                  responseType: "blob",
                }
              );

              this.triggerFileDownload(
                response.data,
                this.getDownloadFilename(response, item.pi_no)
              );
            }

            this.$swal.close();
            this.success("Download file PI berhasil.");
            await this.loadItems();
          } catch (error) {
            this.$swal.close();
            this.handleRequestError(error, "Gagal download file PI.");
          }
        },
      });
    },
    async downloadMultiSheet() {
      if (!this.canDownloadMultiSheet) {
        this.error("Pilih minimal satu PI terlebih dahulu.");
        return;
      }

      const piNos = this.selectedInvoices.map((item) => item.pi_no);

      this.$swal({
        icon: "warning",
        title: "Please Wait",
        text: "Don't Close This Page !!!",
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCancelButton: false,
        showConfirmButton: false,
        didOpen: async () => {
          this.$swal.showLoading();

          try {
            const response = await this.$http.get(
              `${this.proformaInvoiceEndpoint}v1/company-list/download-excel-proforma-invoice-merge`,
              {
                params: {
                  customer_id: this.customer.company_code,
                  pi_nos: piNos,
                },
                data: {
                  customer_id: this.customer.company_code,
                  pi_nos: piNos,
                },
                responseType: "blob",
              }
            );

            this.triggerFileDownload(
              response.data,
              this.getDownloadFilename(response, `multi_sheet_${this.customer.company_code}`)
            );

            this.$swal.close();
            this.success("Download Multi Sheet berhasil.");
            await this.loadItems();
          } catch (error) {
            this.$swal.close();
            this.handleRequestError(error, "Gagal download Multi Sheet.");
          }
        },
      });
    },
    async confirmSelected() {
      if (!this.selectedInvoices.length) {
        this.error("Pilih minimal satu PI terlebih dahulu.");
        return;
      }


      const result = await this.$swal({
        title: this.$t("areYouSure"),
        text: `PI ${this.selectedInvoices.length} akan di confirm.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, confirm",
        cancelButtonText: "Batal",
      });

      if (!result.value) {
        return;
      }

      try {
        for (const item of this.selectedInvoices) {
          await this.$http.get(
            `${this.proformaInvoiceEndpoint}v1/company-list/posted-status/${item.pi_no}`
          );
        }

        this.$swal.close();
        this.success("Confirm berhasil.");
        await this.loadItems();
      } catch (error) {
        this.$swal.close();
        this.handleRequestError(error, "Gagal melakukan confirm.");
      }


    },
    async deleteInvoice(row) {
      const result = await this.$swal({
        title: this.$t("areYouSure"),
        text: `PI ${row.pi_no} akan dihapus.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
      });

      if (!result.value) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$http.delete(
          `${this.proformaInvoiceEndpoint}v1/company-list/delete-proforma-invoice`,
          {
            data: {
              customer_id: this.customer.company_code,
              pi_no: row.pi_no,
            },
          }
        );

        if (response.data?.status === 200) {
          this.success(response.data?.datas?.messages || "Hapus sukses.");
          this.selectedInvoices = this.selectedInvoices.filter(
            (item) => item.pi_no !== row.pi_no
          );
          await this.loadItems();
          return;
        }

        this.error(response.data?.message || "Gagal menghapus proforma invoice.");
      } catch (error) {
        this.handleRequestError(error, "Gagal menghapus proforma invoice.");
      } finally {
        this.isLoading = false;
      }
    },
    async loadCustomerInfo() {
      if (this.$route.query.name && this.$route.query.sap_code) {
        return;
      }

      try {
        const response = await this.$http.get(
          `${this.proformaInvoiceEndpoint}v1/company-list/index`,
          {
            params: {
              company_code: this.$route.params.id,
              page: 1,
              limit: 1,
            },
          }
        );
        const company = response.data?.datas?.data?.[0];

        if (company) {
          this.customer = {
            company_code: company.code || this.$route.params.id || "-",
            customer_name: company.name || this.$route.params.id || "-",
            sap_code: company.sap_code || "-",
          };
        }
      } catch (error) {
        this.handleRequestError(error, "Gagal load customer detail.");
      }
    },
    mapInvoiceRow(item) {
      return {
        pi_no: item.pi_no || item.pi_number || "-",
        status: String(item.pi_status || item.status || "-").toUpperCase(),
        branch:
          item.branch ||
          item.branch_name ||
          item.company_setting?.branch?.branch_name ||
          "-",
        created_at: item.created_at || item.create_at || "-",
        entries: Number(item.total_count || item.entries || 0),
        total_summary:
          item.total_summary ?? item.total_rate ?? item.total ?? item.rate ?? 0,
        template_name:
          item.template_name ||
          item.template?.template_name ||
          item.template?.name ||
          "-",
        raw: item,
      };
    },
    async loadItems() {
      if (!this.$route.params.id) {
        this.error("Company code tidak ditemukan.");
        return;
      }

      this.isLoading = true;

      const params = {
        company_code: this.$route.params.id,
        page: this.serverParams.page,
        limit: this.serverParams.per_page,
      };

      if (this.filters.pi_no) {
        params.pi_no = this.filters.pi_no;
      }

      if (this.filters.load_id) {
        params.load_id = this.filters.load_id;
      }

      if (this.filters.status) {
        params.status = this.filters.status;
      }

      if (this.filters.date_from) {
        params.date_from = this.filters.date_from;
      }

      if (this.filters.date_to) {
        params.date_to = this.filters.date_to;
      }

      try {
        const response = await this.$http.get(
          `${this.proformaInvoiceEndpoint}v1/company-list/detail`,
          { params }
        );
        const datas = response.data?.datas || {};

        this.invoices = (datas.data || []).map((item) => this.mapInvoiceRow(item));
        this.totalRecords = datas.total || 0;
        this.selectedInvoices = [];
      } catch (error) {
        this.invoices = [];
        this.totalRecords = 0;
        this.selectedInvoices = [];
        this.handleRequestError(error, "Gagal load PI detail.");
      } finally {
        this.isLoading = false;
      }
    },
    handleRequestError(error, fallbackMessage) {
      if (error?.response?.status === 500) {
        this.$router.push("/server-error");
        return;
      }

      if (error?.response?.status === 404) {
        this.$router.push("/page-not-found");
        return;
      }

      this.error(
        error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        fallbackMessage
      );
    },
    fetchIt() {
      const userDatas = this.$getUserInfo ? this.$getUserInfo() : null;

      if (!userDatas?.sub?.permision_role) {
        this.loadCustomerInfo();
        this.loadItems();
        return;
      }

      let rolePermision = null;

      userDatas.sub.permision_role.forEach((permision) => {
        if (
          permision.permission.controller === "TemplatePIController" ||
          permision.permission.controller === "ProformaInvoiceController"
        ) {
          rolePermision = permision;
        }
      });

      if (rolePermision && !rolePermision.method_read) {
        this.$router.push("/authorized-error");
        return;
      }

      if (rolePermision) {
        this.permision_role = rolePermision;
      }

      this.loadCustomerInfo();
      this.loadItems();
    },
    error(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      Toast.fire({
        icon: "error",
        title: message,
      });
    },
    success(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      Toast.fire({
        icon: "success",
        title: message,
      });
    },
  },
  mounted() {
    this.fetchIt();
  },
};
</script>

<style scoped>
.customer-detail-card {
  border: 1px solid #dfe3ea;
}

.bg-soft-primary {
  background: rgba(13, 110, 253, 0.12);
}

.bg-soft-warning {
  background: rgba(255, 193, 7, 0.18);
}

.bg-soft-success {
  background: rgba(25, 135, 84, 0.15);
}
</style>
